import { BodyText, Skeleton, Tooltip } from "@/components/ui";
import { useS3Download } from "@/network/useS3Download";
import { Link, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import type * as fhirTypes from "fhir/r4";
import { memo } from "react";
import { Document, Page } from "react-pdf";
import { formatUploadedString } from "../utils";

interface FilePreviewProps {
  file: fhirTypes.DocumentReference;
}

const FilePreviewComponent = ({ file }: FilePreviewProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data, isLoading } = useS3Download({
    patientId: patientId as string,
    path: file?.content?.[0]?.attachment?.url || "",
    enabled: Boolean(file?.content?.[0]?.attachment?.url),
  });

  if (isLoading || !data || !file.id)
    return (
      <div data-testid="file-preview-skeleton">
        <Skeleton className="aspect-square w-full" />
        <Skeleton className="h-8 w-full mt-2" />
      </div>
    );

  const src = URL.createObjectURL(data);
  const uploadTimestamp = file.content[0].attachment.creation || "";
  const name = file.content[0].attachment.title;
  const uploader = file.authenticator?.display;

  return (
    <div>
      <Link
        to="/patient/$patientId/files/$fileId"
        params={{ patientId: patientId as string, fileId: file.id }}
      >
        {data.type !== "application/pdf" ? (
          <div className="aspect-square w-full bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
            <img
              alt="file preview"
              className="w-full h-full object-cover"
              src={src}
            />
          </div>
        ) : (
          <Document
            key={src}
            file={src}
            className="aspect-square bg-tertiary rounded-lg flex items-center justify-center overflow-hidden"
            loading={<Skeleton className="aspect-square" />}
          >
            <Page
              loading={<Skeleton className="h-96 w-64" />}
              className="mt-16 shadow-lg"
              height={250}
              pageIndex={0}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        )}
      </Link>

      <Tooltip
        side="bottom"
        tooltipContentText={`${name}\n${formatUploadedString(uploader, uploadTimestamp)}`}
        contentClassName="whitespace-pre-wrap text-center"
        triggerClassName="flex flex-col items-center mt-3"
      >
        <BodyText size="sm" className="truncate w-full text-center">
          {name}
        </BodyText>
        <BodyText size="sm">
          {format(new Date(uploadTimestamp), "MM/dd/yyyy")}
        </BodyText>
      </Tooltip>
    </div>
  );
};

export const FilePreview = memo(FilePreviewComponent);
